// ── /stats command implementation ────────────────────────────────────────────

import type { ExtensionContext } from '@earendil-works/pi-coding-agent';
import { StatsStore } from './store.js';
import { StatsCollector } from './collector.js';
import { formatCost, formatTokens, formatPercent, formatDuration, formatTable } from './format.js';

export async function handleStatsCommand(args: string, ctx: ExtensionContext, store: StatsStore, collector: StatsCollector): Promise<void> {
  // Direct subcommands
  if (args.trim() === 'reset') {
    store.reset();
    ctx.ui.notify('Session stats reset.', 'info');
    return;
  }

  const choice = await ctx.ui.select('pi-stats-insight', [
    'Tokens & Cost',
    'Cache Performance',
    'Tools',
    'Context & Compaction',
    'Latency & Errors',
  ]);

  if (!choice) return;

  switch (choice) {
    case 'Tokens & Cost': showTokensAndCost(ctx, store); break;
    case 'Cache Performance': showCache(ctx, store); break;
    case 'Tools': showTools(ctx, store); break;
    case 'Context & Compaction': showContext(ctx, store, collector); break;
    case 'Latency & Errors': showLatency(ctx, store); break;
  }
}

function showTokensAndCost(ctx: ExtensionContext, store: StatsStore): void {
  const s = store.getTokenSummary();
  const lines = [
    `Input:          ${formatTokens(s.input)} (${formatCost(s.costInput)})`,
    `Output:         ${formatTokens(s.output)} (${formatCost(s.costOutput)})`,
    `Cache Read:     ${formatTokens(s.cacheRead)} (${formatCost(s.costCacheRead)})`,
    `Cache Write:    ${formatTokens(s.cacheWrite)} (${formatCost(s.costWrite)})`,
    `─`.repeat(40),
    `Total tokens:   ${formatTokens(s.total)}`,
    `Total cost:     ${formatCost(s.costTotal)}`,
  ];
  ctx.ui.notify(lines.join('\n'), 'info');
}

function showCache(ctx: ExtensionContext, store: StatsStore): void {
  const c = store.getCacheSummary();
  const lines = [
    `Overall hit rate: ${formatPercent(c.hitRate)} (${formatTokens(c.cacheRead)} read / ${formatTokens(c.cacheWrite)} write)`,
    `Requests: ${c.totalRequests}`,
    `─`.repeat(40),
  ];

  for (const [prov, data] of Object.entries(c.byProvider)) {
    lines.push(`${prov}: ${formatPercent(data.hitRate)} (${data.requests} req)`);
  }

  ctx.ui.notify(lines.join('\n'), 'info');
}

function showTools(ctx: ExtensionContext, store: StatsStore): void {
  const t = store.getToolSummary();
  const headers = ['Tool', 'Calls', 'Errors', 'Avg ms', 'Result sz'];
  const rows = Object.entries(t.byTool).map(([name, data]) => [
    name, `${data.calls}`, `${data.errors}`, `${Math.round(data.avgMs)}`, `${formatTokens(data.totalResultSize)}`,
  ]);

  const msg = `Total: ${t.totalCalls} calls, ${t.totalErrors} errors, avg ${formatDuration(t.avgDurationMs)}\n\n${formatTable(headers, rows)}`;
  ctx.ui.notify(msg, 'info');
}

function showContext(ctx: ExtensionContext, store: StatsStore, collector: StatsCollector): void {
  const stats = store.getStats();
  const contextUsage = ctx.getContextUsage();
  const window = ctx.model?.contextWindow ?? 0;

  const lines = [
    `Context: ${contextUsage?.tokens ?? '?'} / ${formatTokens(window)} (${contextUsage?.percent ?? '?'}%)`,
    `Compaction events: ${stats.compactionRecords.length}`,
    `─`.repeat(40),
  ];

  for (const c of stats.compactionRecords.slice(-3)) {
    lines.push(`  Before: ${c.beforeTokens ?? '?'} tok → After: ${c.afterTokens ?? '?'} tok`);
  }

  ctx.ui.notify(lines.join('\n'), 'info');
}

function showLatency(ctx: ExtensionContext, store: StatsStore): void {
  const stats = store.getStats();
  const t = store.getToolSummary();

  const lines = [
    `Avg tool duration: ${formatDuration(t.avgDurationMs)}`,
    `Errors: ${stats.errorRecords.length}`,
    `─`.repeat(40),
  ];

  for (const e of stats.errorRecords.slice(-5)) {
    lines.push(`  HTTP ${e.status} @ ${new Date(e.ts).toLocaleTimeString()}`);
  }

  ctx.ui.notify(lines.join('\n'), 'info');
}